import React from 'react';
import { Card, Row, Col, Badge, Alert, Table } from 'react-bootstrap';
import { FaCalendarAlt, FaMoneyBillWave, FaTrophy, FaHistory } from 'react-icons/fa';
import axios from 'axios';

const OwnershipHistoryForm = ({ vehicle, mode = 'display' }) => {

    const [periodDocuments, setPeriodDocuments] = React.useState({});
    const [loadingDocuments, setLoadingDocuments] = React.useState(false);

    React.useEffect(() => {
        if (vehicle?._id) {
            fetchPeriodDocuments();
        }
    }, [vehicle?._id]);

    const fetchPeriodDocuments = async () => {
        try {
            setLoadingDocuments(true);
            const api = (await import('../../services/api')).default;
            const response = await api.get(`/documents/vehicle/${vehicle._id}/by-periods`);

            if (response.data.success) {
                const docsMap = {};
                response.data.periodDocuments.forEach(pd => {
                    docsMap[pd.periodId] = pd.documents;
                });
                setPeriodDocuments(docsMap);
            }
        } catch (error) {
            console.error('Period belgeleri yüklenemedi:', error);
        } finally {
            setLoadingDocuments(false);
        }
    };

    // Helper functions
    const formatNumber = (num) => {
        if (!num) return '0';
        return num.toLocaleString('tr-TR');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Devam ediyor';
        return new Date(dateStr).toLocaleDateString('tr-TR');
    };

    const calculateDaysOwned = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = endDate ? new Date(endDate) : new Date();
        const diffTime = end - start;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    // ✅ FIX: Sadece ownershipPeriods kullan, fallback kaldır
    const periods = vehicle.ownershipPeriods || [];

    // ✅ FALLBACK: Eğer ownershipPeriods yoksa empty state göster
    if (periods.length === 0) {
        return (
            <Alert variant="warning">
                <FaHistory className="me-2" />
                Bu araç için sahiplik geçmişi bulunamadı.
                <br />
                <small className="text-muted">
                    Migration sonrası oluşturulmuş olabilir. Araç detaylarını güncelleyerek sahiplik geçmişi oluşturabilirsiniz.
                </small>
            </Alert>
        );
    }

    // Calculate totals - SADECE ownershipPeriods'dan
    const totalPeriods = periods.length;
    const totalPurchaseAmount = periods.reduce((sum, p) => sum + (p.purchasePrice || 0), 0);
    const totalSalesAmount = periods.reduce((sum, p) => sum + (p.salePrice || 0), 0);
    const totalExpenses = periods.reduce((sum, p) => {
        const periodExpenses = p.expenses?.reduce((expSum, exp) => expSum + (exp.amount || 0), 0) || 0;
        return sum + periodExpenses;
    }, 0);
    const totalProfit = totalSalesAmount - totalPurchaseAmount - totalExpenses;


    return (
        <div>
        
            {/* Summary Cards */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <FaHistory className="text-primary mb-2" size={24} />
                            <h5 className="mb-1">{totalPeriods}</h5>
                            <small className="text-muted">Sahiplik Dönemi</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <FaMoneyBillWave className="text-success mb-2" size={24} />
                            <h5 className="mb-1">{formatNumber(totalSalesAmount)} ₺</h5>
                            <small className="text-muted">Toplam Satış</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <FaMoneyBillWave className="text-danger mb-2" size={24} />
                            <h5 className="mb-1">{formatNumber(totalPurchaseAmount)} ₺</h5>
                            <small className="text-muted">Toplam Alış</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center">
                        <Card.Body>
                            <FaTrophy className={`mb-2 ${totalProfit >= 0 ? 'text-success' : 'text-danger'}`} size={24} />
                            <h5 className={`mb-1 ${totalProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                {formatNumber(totalProfit)} ₺
                            </h5>
                            <small className="text-muted">Net Kar/Zarar</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Timeline */}
            <Card>
                <Card.Header>
                    <h6 className="mb-0">
                        <FaCalendarAlt className="me-2" />
                        Sahiplik Süreci ({totalPeriods} Dönem)
                    </h6>
                </Card.Header>
                <Card.Body>
                    <div className="timeline">
                        {periods.map((period, index) => {
                            const periodExpenses = period.expenses?.reduce((sum, exp) => sum + (exp.amount || 0), 0) || 0;
                            const periodProfit = (period.salePrice || 0) - (period.purchasePrice || 0) - periodExpenses;
                            const daysOwned = calculateDaysOwned(period.purchaseDate, period.saleDate);

                            return (
                                <Card key={`period-${period.period || index + 1}`} className="mb-3" style={{ borderLeft: `4px solid ${period.status === 'sold' ? '#28a745' : '#007bff'}` }}>
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <h6 className="mb-1">
                                                    Period {period.period || index + 1}
                                                    {period.plateNumber && (
                                                        <Badge bg="secondary" className="ms-2">
                                                            {period.plateNumber}
                                                        </Badge>
                                                    )}
                                                    <Badge bg={period.status === 'sold' ? 'success' : 'primary'} className="ms-2">
                                                        {period.status === 'sold' ? 'Satıldı' : 'Aktif'}
                                                    </Badge>
                                                    {/* ✅ CURRENT PERIOD INDICATOR */}
                                                    {index === periods.length - 1 && (
                                                        <Badge bg="warning" className="ms-1">Mevcut Durum</Badge>
                                                    )}
                                                </h6>
                                                <small className="text-muted">
                                                    {daysOwned} gün sahip olundu
                                                </small>
                                            </div>
                                            <div className="text-end">
                                                <div className={`fw-bold ${periodProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                                    {periodProfit >= 0 ? '+' : ''}{formatNumber(periodProfit)} ₺
                                                </div>
                                                <small className="text-muted">Kar/Zarar</small>
                                            </div>
                                        </div>

                                        <Row>
                                            {/* Purchase Info */}
                                            <Col md={6}>
                                                <div className="border-end pe-3">
                                                    <h6 className="text-success mb-2">ALIŞ BİLGİLERİ</h6>
                                                    <Table size="sm" className="mb-0">
                                                        <tbody>
                                                            <tr>
                                                                <td><strong>Tarih:</strong></td>
                                                                <td>{formatDate(period.purchaseDate)}</td>
                                                            </tr>
                                                            <tr>
                                                                <td><strong>Fiyat:</strong></td>
                                                                <td className="fw-bold">{formatNumber(period.purchasePrice)} ₺</td>
                                                            </tr>
                                                            <tr>
                                                                <td><strong>Satıcı:</strong></td>
                                                                <td>{period.purchasedFrom?.name || 'Bilgi yok'}</td>
                                                            </tr>
                                                            {period.purchasedFrom?.phone && (
                                                                <tr>
                                                                    <td><strong>Telefon:</strong></td>
                                                                    <td>{period.purchasedFrom.phone}</td>
                                                                </tr>
                                                            )}
                                                            {period.purchasedFrom?.idNumber && (
                                                                <tr>
                                                                    <td><strong>TC/VKN:</strong></td>
                                                                    <td>{period.purchasedFrom.idNumber}</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Col>

                                            {/* Sale Info */}
                                            <Col md={6}>
                                                <div className="ps-3">
                                                    <h6 className="text-primary mb-2">SATIŞ BİLGİLERİ</h6>
                                                    {period.status === 'sold' && period.salePrice > 0 ? (
                                                        <Table size="sm" className="mb-0">
                                                            <tbody>
                                                                <tr>
                                                                    <td><strong>Tarih:</strong></td>
                                                                    <td>{formatDate(period.saleDate)}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>Fiyat:</strong></td>
                                                                    <td className="fw-bold">{formatNumber(period.salePrice)} ₺</td>
                                                                </tr>
                                                                <tr>
                                                                    <td><strong>Alıcı:</strong></td>
                                                                    <td>{period.soldTo?.name || 'Bilgi yok'}</td>
                                                                </tr>
                                                                {period.soldTo?.phone && (
                                                                    <tr>
                                                                        <td><strong>Telefon:</strong></td>
                                                                        <td>{period.soldTo.phone}</td>
                                                                    </tr>
                                                                )}
                                                                {period.soldTo?.idNumber && (
                                                                    <tr>
                                                                        <td><strong>TC/VKN:</strong></td>
                                                                        <td>{period.soldTo.idNumber}</td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </Table>
                                                    ) : (
                                                        <Alert variant="info" className="mb-0">
                                                            <small>
                                                                {index === periods.length - 1
                                                                    ? "Araç henüz satılmadı (Mevcut aktif period)"
                                                                    : "Bu period için satış bilgisi yok"
                                                                }
                                                            </small>
                                                        </Alert>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>

                                        {/* Expenses */}
                                        {periodExpenses > 0 && (
                                            <div className="mt-3 pt-3 border-top">
                                                <small className="text-muted">
                                                    <strong>Masraflar:</strong> {formatNumber(periodExpenses)} ₺
                                                    ({period.expenses?.length || 0} kalem)
                                                </small>
                                            </div>
                                        )}
                                        {/* ✅ BELGELER */}
                                        {periodDocuments[period._id] && periodDocuments[period._id].length > 0 && (
                                            <div className="mt-3 pt-3 border-top">
                                                <h6 className="mb-2">📄 Period Belgeleri ({periodDocuments[period._id].length})</h6>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {periodDocuments[period._id].map(doc => (
                                                        <Badge
                                                            key={doc._id}
                                                            bg="secondary"
                                                            className="p-2"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                const url = doc.file?.cloudinaryUrl || doc.url;
                                                                const mimeType = doc.file?.mimeType || doc.category;

                                                                if (mimeType.includes('pdf') || doc.category.includes('sozlesme')) {
                                                                    window.open(`https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`, '_blank');
                                                                } else {
                                                                    window.open(url, '_blank');
                                                                }
                                                            }}                                                        >
                                                            {doc.categoryName || doc.category}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* ✅ PERIOD SUMMARY */}
                                        <div className="mt-2 pt-2 border-top">
                                            <Row>
                                                <Col md={6}>
                                                    <small className="text-muted">
                                                        <strong>Period Özeti:</strong>
                                                        {formatDate(period.purchaseDate)} - {formatDate(period.saleDate)}
                                                    </small>
                                                </Col>
                                                <Col md={6} className="text-end">
                                                    <small className={`fw-bold ${periodProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                                        Net: {formatNumber(periodProfit)} ₺
                                                    </small>
                                                </Col>
                                            </Row>
                                        </div>
                                    </Card.Body>
                                </Card>
                            );
                        })}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default OwnershipHistoryForm;